import {
    formNameLabel,
    scheduleEmbedLabel,
    schedulePreviewEmbed,
} from "@/features/navigation/services/entityDisplayLabel";
import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface MyValidationHistoryEntryT {
    validation_id: string;
    entity_type: "form" | "schedule";
    entity_id: string;
    entity_name: string;
    is_valid: boolean;
    validated_at: string;
    created_at: string;
    /** When the form or schedule content last changed. */
    calculated_updated_at: string | null;
    reason: string | null;
    /**
     * True only when this row is both the entity's *current* validation (nothing
     * newer - resolved or still-pending - has superseded it) and the entity has
     * been edited since. An older, superseded row is never stale, even if the
     * entity has since been edited many times over - only the current one matters,
     * so only it can offer "Revalidate".
     */
    is_stale: boolean;
}

export interface MyValidationHistoryPage {
    rows: MyValidationHistoryEntryT[];
    total: number;
}

export type MyValidationHistoryStatusT = "approved" | "rejected";

export const MY_VALIDATION_HISTORY_PAGE_SIZE = 20;

const myHistorySelect = `validation_id, is_valid, validated_at, created_at, reason, form_validations(form_id, forms(form_name, slug, calculated_updated_at, validation_id)), schedule_validations(schedule_id, schedules(calculated_updated_at, validation_id, ${schedulePreviewEmbed}))`;

/**
 * Every validation the current user has ever *resolved*, newest first, page-based -
 * used for a validator's "your history" view (see validations-feature-concerns.md
 * §4b).
 *
 * Deliberately excludes still-pending rows (`is_valid IS NULL`) - those already show
 * up in "Things to validate", so including them here too would just be the same
 * to-do listed twice under two different labels.
 */
export async function selectMyValidationHistory(
    userId: string,
    page: number,
    status?: MyValidationHistoryStatusT,
    pageSize: number = MY_VALIDATION_HISTORY_PAGE_SIZE
): Promise<MyValidationHistoryPage> {
    const rangeFrom = page * pageSize;
    const rangeTo = rangeFrom + pageSize - 1;

    let query = supabaseClient
        .from("validations")
        .select(myHistorySelect, { count: "exact" })
        .eq("validated_by", userId)
        .not("is_valid", "is", null);

    if (status === "approved") {
        query = query.eq("is_valid", true);
    } else if (status === "rejected") {
        query = query.eq("is_valid", false);
    }

    const { data, error, count } = await query.order("created_at", { ascending: false }).range(rangeFrom, rangeTo);
    if (error) throw error;

    const rows = (data ?? []).flatMap((row): MyValidationHistoryEntryT[] => {
        if (row.is_valid === null || row.validated_at === null) return [];

        const isStale = (calculatedUpdatedAt: string | null, currentValidationId: string | null) =>
            Boolean(
                currentValidationId === row.validation_id &&
                calculatedUpdatedAt &&
                calculatedUpdatedAt > row.validated_at!
            );

        const formLink = row.form_validations?.[0];
        if (formLink?.form_id) {
            return [
                {
                    validation_id: row.validation_id,
                    entity_type: "form",
                    entity_id: formLink.form_id,
                    entity_name: formNameLabel(formLink.forms),
                    is_valid: row.is_valid,
                    validated_at: row.validated_at,
                    created_at: row.created_at,
                    calculated_updated_at: formLink.forms?.calculated_updated_at ?? null,
                    reason: row.reason,
                    is_stale: isStale(
                        formLink.forms?.calculated_updated_at ?? null,
                        formLink.forms?.validation_id ?? null
                    ),
                },
            ];
        }
        const scheduleLink = row.schedule_validations?.[0];
        if (scheduleLink?.schedule_id) {
            return [
                {
                    validation_id: row.validation_id,
                    entity_type: "schedule",
                    entity_id: scheduleLink.schedule_id,
                    entity_name: scheduleEmbedLabel(scheduleLink.schedules),
                    is_valid: row.is_valid,
                    validated_at: row.validated_at,
                    created_at: row.created_at,
                    calculated_updated_at: scheduleLink.schedules?.calculated_updated_at ?? null,
                    reason: row.reason,
                    is_stale: isStale(
                        scheduleLink.schedules?.calculated_updated_at ?? null,
                        scheduleLink.schedules?.validation_id ?? null
                    ),
                },
            ];
        }
        return [];
    });

    return { rows, total: count ?? 0 };
}

export const myValidationHistoryQueryOptions = (
    userId: string,
    page: number,
    status?: MyValidationHistoryStatusT,
    pageSize: number = MY_VALIDATION_HISTORY_PAGE_SIZE
) =>
    queryOptions({
        queryKey: ["validations", "history", "mine", userId, page, pageSize, status ?? "all"],
        queryFn: () => selectMyValidationHistory(userId, page, status, pageSize),
        enabled: Boolean(userId),
    });

export function useMyValidationHistoryQuery(
    userId: string,
    page: number,
    status?: MyValidationHistoryStatusT,
    pageSize: number = MY_VALIDATION_HISTORY_PAGE_SIZE
) {
    return useQuery(myValidationHistoryQueryOptions(userId, page, status, pageSize));
}
